import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import api from '../utils/api'; 
import '../style.css';
import CircularProgress from '@mui/material/CircularProgress';

const MonthlyAnalysisChart = ({range, selectedRegions, selectedDistricts, monthYear}) => {
    const [loading, setLoading] = useState(true);

    const [livestockChartData, setLivestockChartData] = useState({
        series: [],
        options: {
            chart: {
                type: 'line',
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                        selection: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                        pan: false,
                        reset: false,
                    }
                }
            },
            stroke: {
                width: [0, 2, 2, 2, 2]  // width 0 for bar (last series)
            },
            dataLabels: {
                // enabled: true,
                // enabledOnSeries: [1]
            },
            labels: [],
            title: {
                text: 'Monthly Livestock Served & Average Pump Hours'
            },
            xaxis: {
                type: 'category'
            },
            yaxis: [
                {
                    title: {
                        text: 'Average Pump Hours',
                    },
                    min: 0,
                    // ogarithmic: true
                    forceNiceScale: true,
                },
                {
                    opposite: true,
                    title: {
                        text: 'Livestock Served'
                    },
                    labels:{
                        formatter: (val) => Math.round(Number(val)).toLocaleString('en-US'),
                    },
                    // min: 0,
                    // max:800,
                    forceNiceScale: true,
                    // logarithmic: true
                }
            ],
            tooltip: {
                shared: true,
                intersect: false
            },

            legend: {
                show: true,
                position: 'bottom',           // Place legend at the bottom
                horizontalAlign: 'center',   // Center horizontally
                clusterGroupedSeriesOrientation: 'horizontal',
                floating: false,
                fontSize: '9px',
                itemMargin: {
                    horizontal: 2,
                    vertical: 0                // Prevent vertical stacking
                },
                markers: {
                    width: 10,
                    height: 10
                }
            },
        }
    });

    const [householdChartData, setHouseholdChartData] = useState({
        series: [],
        options: {
            chart: {
                type: 'line',
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                        selection: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                        pan: false,
                        reset: false,
                    }
                }
            },
            stroke: {
                width: [0, 2, 2, 2, 2]  // width 0 for bar (last series)
            },
            dataLabels: {
                // enabled: true,
                // enabledOnSeries: [1]
            },
            labels: [],
            title: {
                text: 'Monthly Household Served & Average Water Price per Barrel (USD)'
            },
            xaxis: {
                type: 'category'
            },
            yaxis: [
                {
                    title: {
                        text: 'Average Water Price/Barrel (USD)',
                    },
                    min: 0,
                    // ogarithmic: true
                    forceNiceScale: true,
                },
                {
                    opposite: true,
                    title: {
                        text: 'Houshold Served'
                    },
                    labels:{
                        formatter: (val) => Math.round(Number(val)).toLocaleString('en-US'),
                    },
                    min: 0,
                    forceNiceScale: true,
                    // max:800
                    // logarithmic: true
                }
            ],
            tooltip: {
                shared: true,
                intersect: false,
                y: [
                    {
                        formatter: (val) => val,
                    },
                    {
                        formatter: (val) => Math.round(Number(val)).toLocaleString('en-US'),
                    }
                ]
            },

            legend: {
                show: true,
                position: 'bottom',           // Place legend at the bottom
                horizontalAlign: 'center',   // Center horizontally
                // clusterGroupedSeriesOrientation: 'horizontal',
                floating: false,
                fontSize: '9px',
                itemMargin: {
                    horizontal: 2,
                    vertical: 0                // Prevent vertical stacking
                },
                markers: {
                    width: 10,
                    height: 10
                }
            },
        }
    });
    
    useEffect(() => {
        if (range[0] != null) {
            setLoading(true);
            const controller = new AbortController();
            let start_date = new Date(range[0]).toISOString().slice(0, 10);
            let end_date = new Date(range[1]).toISOString().slice(0, 10);
            const monthsParam = monthYear.join(','); 
            let url = "monthly-analysis-chart/";
            api.get(url, {
                params: {
                    start: start_date,
                    end: end_date,
                    region:JSON.stringify(selectedRegions),
                    district:JSON.stringify(selectedDistricts),
                    months : monthsParam
                },
                signal: controller.signal
            }).then(res => {
                const data = JSON.parse(res.data);  // or res.data directly if already JSON
                setLivestockChartData(prev => ({
                    series: [{
                        name: 'Pump Hours',
                        type: 'column',
                        yAxisIndex: 1,
                        color: 'rgba(128, 128, 128, 0.6)', //
                        data: data.map(d => d.avg_pump_hours)
                    },{
                        name: 'Camel Served',
                        type: 'line',
                        data: data.map(d => d.avg_camel_served)
                    },
                    {
                        name: 'Sheep Served',
                        type: 'line',
                        data: data.map(d => d.avg_sheep_served)
                    },
                    {
                        name: 'Cattle Served',
                        type: 'line',
                        data: data.map(d => d.avg_cattle_served)
                    },
                    {
                        name: 'Donkey Served',
                        type: 'line',
                        data: data.map(d => d.avg_donkey_served)
                    }],  
                    options: {
                        ...prev.options,
                        labels: data.map(d => dayjs(d.month).format('MMM YYYY')),
                    }
                }));

                setHouseholdChartData(prev => ({
                    series: [{
                        name: 'Water Price/Barrel',
                        type: 'column',
                        yAxisIndex: 1,
                        color: 'rgba(128, 128, 128, 0.6)', //
                        data: data.map(d => d.avg_price)
                    },{
                        name: 'Houshold Served',
                        type: 'line',
                        data: data.map(d => d.avg_houshold)
                    }],  
                    options: {
                        ...prev.options,
                        labels: data.map(d => dayjs(d.month).format('MMM YYYY')),
                    }
                }));
            }).catch((err) => {
                console.error('Failed to fetch reports:', err);
            })
            .finally(() => setLoading(false));
            return () => {
                controller.abort();
            };
        }
    }, [monthYear, selectedRegions, selectedDistricts]);


    return (
        <>
        <div id='wrap1'> 
            <div id="chart-malfunction" style={{ minHeight: 352, width: '100%' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <ReactApexChart key={JSON.stringify(livestockChartData.series)} options={livestockChartData.options} series={livestockChartData.series} type="line" width={'100%'} height={310} />
                )}
            </div>
            <div id="chart-malfunction" style={{ minHeight: 352, width: '100%' }}>
                 {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <ReactApexChart key={JSON.stringify(householdChartData.series)} options={householdChartData.options} series={householdChartData.series} type="line" width={'100%'} height={310} />
                )}
            </div>
        </div>
        </>
    );
};

export default MonthlyAnalysisChart;